import { v4 as uuidv4 } from "uuid";
import auth0 from "@gtmhub/auth0";

import { IAuthResponse } from "./models";
import { AuthCookie } from "./cookie";
import { isLocalhost, isSecure } from "@gtmhub/env";

const setAuthCookie = (args: { domain: string; secure: boolean; state: string; nonce: string; appState?: Record<string, string>; isMsTeamsState?: boolean }) => {
  const maxAgeInSecs = 10 * 60; // 10mins
  const authCookie = new AuthCookie(args.domain, args.secure, args.isMsTeamsState);
  authCookie.set({ state: args.state, nonce: args.nonce, appState: args.appState }, { maxAgeInSecs });
};

const auth0Scope = "openid email";
const auth0ResponseType = "token id_token";
const auth0ResponseMode = "form_post";

export interface IConnection {
  connectionName: string;
  domain: string;
}
export function buildAuth0LoginUrl(
  auth: IAuthResponse,
  email: string,
  options: { appState?: Record<string, string>; isMsTeamsState?: boolean; bypassDomainCheck?: boolean; connection?: IConnection } = {}
): string {
  const state = uuidv4();
  const nonce = uuidv4();

  // Gtmhub domain includes the protocol and the host
  // e.g., https://hello.gtmhub.com
  const [gtmhubProtocol, gtmhubHost] = auth.gtmhubDomain.split("//");
  let cookieDomain = gtmhubHost;
  let secure = isSecure(gtmhubProtocol);
  let redirectUrl = auth.redirectUrl;

  const { protocol, host, hostname } = window.location;
  if (options.bypassDomainCheck || isLocalhost(host)) {
    // on localhost we allow only returns to localhost
    // this helps also when we work against staging API
    cookieDomain = hostname;
    secure = isSecure(protocol);
    redirectUrl = `${protocol}//${host}/login/callback`;

    const warning = options.bypassDomainCheck
      ? "Auth0 redirectUri will be rewritten because of bypassing domain check"
      : "Auth0 redirectUri will be rewritten because you are working on localhost";
    console.warn(warning, { old: auth.redirectUrl, new: redirectUrl });
  }

  setAuthCookie({ domain: cookieDomain, secure, state, nonce, appState: options.appState, isMsTeamsState: options.isMsTeamsState });

  const webAuth = new auth0.Authentication({
    clientID: auth.clientId,
    domain: auth.authDomain,
    audience: auth.audience,
    scope: auth0Scope,
    responseType: auth0ResponseType,
    responseMode: auth.responseMode,
    redirectUri: redirectUrl,
  });
  return webAuth.buildAuthorizeUrl({
    connection: options.connection ? options.connection.connectionName : null,
    login_hint: email,
    allow_sign_up: "false",
    state,
    nonce,
  });
}

export function navigateToAuth0SignUp(auth0Config: { clientId: string; domain: string; audience: string }): void {
  const state = uuidv4();
  const nonce = uuidv4();

  const { protocol, host, hostname, search } = window.location;
  const secure = isSecure(protocol);
  setAuthCookie({ domain: hostname, secure, state, nonce });

  const allowLogin = search.indexOf("invitationKey") < 0;
  const invitationKey = allowLogin ? "" : "?inviteKey=" + search.split("invitationKey=")[1];

  const webAuth = new auth0.WebAuth({
    clientID: auth0Config.clientId,
    domain: auth0Config.domain,
    audience: auth0Config.audience,
    scope: auth0Scope,
    responseType: auth0ResponseType,
    responseMode: auth0ResponseMode,
    redirectUri: `${protocol}//${host}/login/callback${invitationKey}`,
  });
  webAuth.authorize({ state, nonce, initialScreen: "signUp", allowLogin: `${allowLogin}` });
}
